/**
 * MarketSnapshotCard — Phase 9 + Phase 10 dual-source AVM panel.
 *
 * Renders nothing when ``deal.market_data`` is missing (non-US, both sources
 * unavailable). Otherwise shows:
 *   - Side-by-side RentCast vs ATTOM values + ranges
 *   - Blended midpoint + agreement band (color dot)
 *   - Single-source fallback when only one source returned data
 *   - User-vs-market deltas with tone (green/amber/red)
 *   - "Refresh from RentCast/ATTOM" button (landlord-only)
 */
import { useState } from "react";
import { Lightbulb, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtMoney(n) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return `$${Math.round(n).toLocaleString()}`;
}

function pctDelta(actual, target) {
  if (!target) return null;
  return ((actual - target) / target) * 100;
}

function toneAsking(asking, avm) {
  const d = pctDelta(asking, avm);
  if (d === null) return "slate";
  if (d > 10) return "red";
  if (d <= 0) return "emerald";
  return "amber";
}

function toneRent(userRent, marketRent) {
  const d = pctDelta(userRent, marketRent);
  if (d === null) return "slate";
  if (d < -15) return "red";
  if (d < -5) return "amber";
  return "emerald";
}

const toneStyles = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  red: "border-red-200 bg-red-50 text-red-900",
  slate: "border-slate-200 bg-slate-50 text-slate-700",
};

const AGREEMENT_TONE = {
  strong: { dot: "bg-emerald-500", label: "Strong agreement", color: "text-emerald-700" },
  moderate: { dot: "bg-amber-500", label: "Moderate agreement", color: "text-amber-700" },
  weak: { dot: "bg-orange-500", label: "Weak agreement", color: "text-orange-700" },
  diverged: { dot: "bg-red-500", label: "Sources diverged", color: "text-red-700" },
  single_source: { dot: "bg-slate-400", label: "Single source", color: "text-slate-500" },
};

function SourceRow({ label, value, low, high, formatter }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <div className="text-right">
        <div className="text-sm font-semibold text-slate-900">
          {value ? formatter(value) : <span className="text-slate-400">—</span>}
        </div>
        {value && low && high ? (
          <div className="text-[11px] text-slate-500">
            {formatter(low)} – {formatter(high)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function MarketSnapshotCard({ deal, onRefresh }) {
  const md = deal?.market_data;
  const [refreshing, setRefreshing] = useState(false);

  if (!md) return null;
  const ve = md.value_estimate || null;
  const re = md.rent_estimate || null;
  if (!ve && !re) return null;

  const rc = md.rentcast || null;
  const at = md.attom || null;
  const cv = md.cross_validation || {};
  const agreement = cv.agreement || "single_source";
  const agreementTone = AGREEMENT_TONE[agreement] || AGREEMENT_TONE.single_source;

  // Phase 48.1 — "Asking" here is the seller list, NOT the buyer's
  // counter/effective price. Prefer the backend-stamped display field,
  // fall back to askingFor(deal) which walks the canonical chain.
  const asking = Number(
    deal?.asking_price_display ??
      deal?.original_purchase_price ??
      deal?.property?.asking_price ??
      deal?.purchase_price ??
      0
  );
  const userRent = deal.monthly_rent;
  const blendedValue = ve?.price;
  const blendedRent = re?.rent;

  const askingTone = toneAsking(asking, blendedValue);
  const rentTone = toneRent(userRent, blendedRent);
  const compCount =
    ((rc?.value?.comp_count) || 0) + ((at?.value?.comp_count) || 0);

  const rcValue = rc?.value || null;
  const rcRent = rc?.rent || null;
  const atValue = at?.value || null;
  const atRent = at?.rent || null;
  const showRentCast = !!(rcValue || rcRent);
  const showAttom = !!(atValue || atRent);
  const showBlended = showRentCast && showAttom;

  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/deals/${deal.id}/refresh-market-data`);
      if (data?.market_data) {
        toast.success("Market data refreshed");
        onRefresh?.(data);
      } else {
        toast.info("No new market data available");
      }
    } catch (e) {
      toast.error("Failed to refresh market data");
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <section
      data-testid="market-snapshot-card"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <header className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100">
            <Lightbulb className="h-4 w-4 text-emerald-700" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Market Snapshot</h3>
            <p className="text-xs text-slate-500">
              {showBlended ? "RentCast + ATTOM" : showRentCast ? "RentCast" : "ATTOM"}
              {compCount ? ` · ${compCount} comp${compCount === 1 ? "" : "s"}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium ${agreementTone.color}`}
            data-testid="market-agreement-badge"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${agreementTone.dot}`} />
            {agreementTone.label}
            {cv.delta_pct != null ? ` · ${(cv.delta_pct * 100).toFixed(1)}%` : ""}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="market-snapshot-refresh"
          >
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </header>

      {cv.warning ? (
        <div
          data-testid="market-warning"
          className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
          <span>{cv.warning}</span>
        </div>
      ) : null}

      {showBlended ? (
        <div className="mb-4 grid gap-3 sm:grid-cols-3" data-testid="market-snapshot-multi">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              RentCast
            </p>
            <SourceRow label="Value" value={rcValue?.price} low={rcValue?.low} high={rcValue?.high} formatter={fmtMoney} />
            <SourceRow
              label="Rent /mo"
              value={rcRent?.rent}
              low={rcRent?.low}
              high={rcRent?.high}
              formatter={(n) => `${fmtMoney(n)}`}
            />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              ATTOM
            </p>
            <SourceRow label="Value" value={atValue?.price} low={atValue?.low} high={atValue?.high} formatter={fmtMoney} />
            <SourceRow
              label="Rent /mo"
              value={atRent?.rent}
              low={atRent?.low}
              high={atRent?.high}
              formatter={(n) => `${fmtMoney(n)}`}
            />
          </div>
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3" data-testid="market-snapshot-blended">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">
              Blended midpoint
            </p>
            <SourceRow label="Value" value={blendedValue} low={ve?.low} high={ve?.high} formatter={fmtMoney} />
            <SourceRow
              label="Rent /mo"
              value={blendedRent}
              low={re?.low}
              high={re?.high}
              formatter={(n) => `${fmtMoney(n)}`}
            />
          </div>
        </div>
      ) : (
        <p className="mb-3 text-xs text-slate-500">
          Single source — {showRentCast ? "ATTOM" : "RentCast"} data unavailable for this property.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {blendedValue ? (
          <div className={`rounded-xl border p-4 ${toneStyles[askingTone]}`} data-testid="market-snapshot-value">
            <p className="text-xs font-medium uppercase tracking-wide opacity-70">Asking vs market</p>
            <p className="mt-1 text-2xl font-semibold">{fmtMoney(asking)}</p>
            <p className="mt-1 text-xs opacity-70">
              vs {fmtMoney(blendedValue)} {showBlended ? "blended AVM" : "AVM"}
              {blendedValue
                ? (() => {
                    const d = pctDelta(asking, blendedValue);
                    if (d === null) return null;
                    const sign = d > 0 ? "+" : "";
                    return (
                      <span className="ml-1 font-medium">
                        ({sign}
                        {d.toFixed(1)}%)
                      </span>
                    );
                  })()
                : null}
            </p>
          </div>
        ) : null}
        {blendedRent ? (
          <div className={`rounded-xl border p-4 ${toneStyles[rentTone]}`} data-testid="market-snapshot-rent">
            <p className="text-xs font-medium uppercase tracking-wide opacity-70">User rent vs market</p>
            <p className="mt-1 text-2xl font-semibold">{fmtMoney(userRent)}/mo</p>
            <p className="mt-1 text-xs opacity-70">
              vs {fmtMoney(blendedRent)}/mo market
              {blendedRent
                ? (() => {
                    const d = pctDelta(userRent, blendedRent);
                    if (d === null) return null;
                    const sign = d > 0 ? "+" : "";
                    return (
                      <span className="ml-1 font-medium">
                        ({sign}
                        {d.toFixed(1)}%)
                      </span>
                    );
                  })()
                : null}
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
